import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Mail, Phone, MapPin,
  Smartphone, Code, Rocket, Shield, ArrowRight, Star, Users,
  Download, Play, Sparkles
} from "lucide-react";
import "@/App.css";

// ==================== APP DATA ====================
const APPS = [
  {
    id: "ezan-vakti",
    name: "Ezan Vakti Plus",
    tagline: "Manevi Hayatınızın Dijital Yol Arkadaşı",
    description: "Namaz vakitleri, kıble pusulası, cami bulucu, Kuran-ı Kerim, sesli dualar ve daha fazlası ücretsiz tek uygulamada.",
    logo: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/wtvdfl2g_iTunesArtwork%402x.png",
    screenshots: [
      "/Ekran görüntüsü 2026-02-24 024542.png",
      "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/scidc5c6_2.png",
    ],
    features: ["Namaz Vakitleri", "Kıble Pusulası", "Cami Bulucu", "Kuran-ı Kerim", "Sesli Dualar"],
    appStore: "https://apps.apple.com/tr/app/ezan-vakti-plus/id6759493457?l=tr",
    playStore: "https://play.google.com/store/apps/details?id=com.merttuna.ezanvaktiplus",
    promoUrl: "/ezan-vakti",
    gradient: "from-cyan-900/50 to-blue-900/50",
    accentColor: "#d4af37"
  },
  {
    id: "deprem-sos",
    name: "Deprem S.O.S",
    tagline: "Acil Durumda Yanınızda",
    description: "Tek tuşla konumunu gönder, konumlu SMS, bildirim ve bluetooth sinyali ile kendini güvene al, deprem düdüğü ve flaşör ile kendinizi belli edin.",
    logo: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/mq8ee097_appstore.png",
    screenshots: [
      "/Ekran görüntüsü 2026-02-24 024949.png",
      "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/u2gasuai_2.png",
    ],
    features: ["Acil SOS", "Konum Paylaş", "Düdük & Flaşör", "Bluetooth Sinyal", "Deprem Alarmı"],
    appStore: "#",
    playStore: "https://play.google.com/store/apps/details?id=com.depremsos.app",
    promoUrl: "/deprem-sos",
    gradient: "from-orange-900/50 to-red-900/50",
    accentColor: "#ff6b35"
  },
  {
    id: "smart-notes",
    name: "Smart Notes AI Pro",
    tagline: "Akıllı Not, Finans, AI Asistan",
    description: "Notlarınızı AI ile düzenleyin, gelir-gider ve yatırım portföyünüzü takip edin, ajandanızı yönetin. Tüm ihtiyaçlarınız tek uygulamada.",
    logo: "/smart-notes-logo.png.png",
    screenshots: [
      "/smart-notes-1.png.jpg",
      "/smart-notes-2.png.jpg",
      "/smart-notes-3.png.jpg",
      "/smart-notes-4.png.jpg",
    ],
    features: ["AI Asistan", "Akıllı Notlar", "Finans Takibi", "Yatırım Portföyü", "Ajanda", "Gelir-Gider"],
    appStore: "https://apps.apple.com/tr/app/smart-notes-ai-pro/id6760021954?l=tr",
    playStore: "#",
    promoUrl: "/smart-notes",
    gradient: "from-indigo-900/50 to-cyan-900/50",
    accentColor: "#8b5cf6"
  }
];

// ==================== PROJECTS DATA ====================
const PROJECTS = [
  {
    id: "zena-akademi",
    name: "Zena Akademi",
    type: "Masaüstü Uygulaması",
    tagline: "Yenilikçi Bir Eğitim Deneyimi",
    description: "Türkiye'nin odaklı ders modülüne sahip online eğitim platformu. Öğretmen ve öğrenci yönetimi, sanal sınıf programı, ders takibi, ödevlendirme, yoklama sistemi, kayıtlı dersler ve site yönetimi ile kapsamlı bir eğitim çözümü.",
    screenshots: Array.from({length: 26}, (_, i) => `/zena-${i + 2}.png`),
    features: ["Sanal Sınıf", "Ders Yönetimi", "Öğrenci Takibi", "Ödevlendirme", "Yoklama", "Dönem Yönetimi", "Duyurular", "Site Yönetimi"],
    technologies: ["Electron", "React", "Node.js"],
    gradient: "from-cyan-900/50 to-indigo-900/50",
    accentColor: "#7c3aed"
  }
];

const SERVICES = [
  { icon: Smartphone, title: "Mobil Uygulama", desc: "iOS ve Android için native uygulamalar" },
  { icon: Code, title: "Web Geliştirme", desc: "Modern ve responsive web siteleri" },
  { icon: Shield, title: "UI/UX Tasarım", desc: "Kullanıcı odaklı arayüz tasarımları" },
  { icon: Rocket, title: "Dijital Pazarlama", desc: "ASO ve uygulama tanıtım stratejileri" }
];

const STATS = [
  //{ value: "50K+", label: "Toplam İndirme" },
  { value: "4.8", label: "Ortalama Puan" },
  { value: "3+", label: "Yayında Uygulama" },
  { value: "7/24", label: "Destek" }
];

// ==================== NAVBAR ====================
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? "bg-[#05050A]/80 backdrop-blur-xl border-b border-white/5 shadow-lg"
        : "bg-transparent"
        }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img
              src="/mt_logo_1_binary.png"
              alt="MT Company Logo"
              className="w-10 h-10 rounded-xl shadow-lg group-hover:shadow-cyan-500/30 transition-all"
            />
            <span className="text-xl font-bold text-white tracking-tight">MT Company</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("hero")} className="nav-link" data-testid="nav-link-home">Ana Sayfa</button>
            <button onClick={() => scrollToSection("apps")} className="nav-link" data-testid="nav-link-apps">Uygulamalar</button>
            <button onClick={() => scrollToSection("projects")} className="nav-link" data-testid="nav-link-projects">Projeler</button>
            <button onClick={() => scrollToSection("about")} className="nav-link" data-testid="nav-link-about">Hakkımızda</button>
            <button onClick={() => scrollToSection("contact")} className="nav-link" data-testid="nav-link-contact">İletişim</button>
            <button
              onClick={() => scrollToSection("contact")}
              className="btn-primary"
              data-testid="nav-cta-btn"
            >
              Proje Başlat
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-[#05050A]/95 backdrop-blur-xl border-b border-white/5"
          >
            <div className="px-4 py-6 space-y-4">
              <button onClick={() => scrollToSection("hero")} className="mobile-nav-link">Ana Sayfa</button>
              <button onClick={() => scrollToSection("apps")} className="mobile-nav-link">Uygulamalar</button>
              <button onClick={() => scrollToSection("about")} className="mobile-nav-link">Hakkımızda</button>
              <button onClick={() => scrollToSection("contact")} className="mobile-nav-link">İletişim</button>
              <button onClick={() => scrollToSection("contact")} className="btn-primary w-full">Proje Başlat</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = () => {
  const [selectedApp, setSelectedApp] = useState(0);

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      {/* Background */}
      <div className="hero-bg">
        <div className="hero-gradient"></div>
        <div className="hero-grid"></div>
        <div className="hero-glow"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-400/20 rounded-full mb-6">
              <Sparkles size={16} className="text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Mobil Uygulama Geliştirme</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Fikirlerinizi{" "}
              <span className="text-gradient-cyan">Dijital</span>{" "}
              Gerçekliğe Dönüştürüyoruz
            </h1>

            <p className="text-xl text-slate-400 mb-8 leading-relaxed max-w-xl">
              iOS ve Android için profesyonel mobil uygulamalar geliştiriyoruz.
              Kullanıcı deneyimini ön planda tutarak, markanızı bir adım öteye taşıyoruz.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => document.getElementById("apps").scrollIntoView({ behavior: "smooth" })}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="hero-cta-btn"
              >
                Uygulamalarımız
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => document.getElementById("contact").scrollIntoView({ behavior: "smooth" })}
                className="btn-secondary"
                data-testid="hero-contact-btn"
              >
                İletişime Geç
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-6 mt-12 pt-12 border-t border-slate-800">
              {STATS.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - App Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative mt-12 lg:mt-0 flex justify-center w-full"
          >
            <div className="relative w-full max-w-sm lg:max-w-none">
              {/* Phone + App Cards Side by Side */}
              <div className="flex flex-col sm:flex-row gap-6 items-center justify-center">
                {/* Phone Mockup */}
                <div className="flex-shrink-0">
                  <div className="phone-mockup">
                    <AnimatePresence mode="wait">
                      <AnimatedPhoneScreenshot
                        key={selectedApp}
                        src={APPS[selectedApp].screenshots[0]}
                        alt={`${APPS[selectedApp].name} Screenshot`}
                        isActive={true}
                      />
                    </AnimatePresence>
                  </div>
                </div>

                {/* App Cards - Stacked Right */}
                <div className="flex flex-col gap-4" style={{ width: '200px' }}>
                  {/* Ezan Vakti */}
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="cursor-pointer"
                    onClick={() => setSelectedApp(0)}
                  >
                    <div className={`app-preview-card transition-all duration-300 ${selectedApp === 0 ? 'ring-2 ring-cyan-400 shadow-lg shadow-cyan-500/20' : 'opacity-70 hover:opacity-100'}`}>
                      <img src={APPS[0].logo} alt="Ezan Vakti Plus" className="w-14 h-14 rounded-2xl shadow-lg" />
                      <h3 className="text-white font-semibold mt-2 text-sm">{APPS[0].name}</h3>
                      <p className="text-slate-400 text-xs">{APPS[0].tagline}</p>
                    </div>
                  </motion.div>

                  {/* Smart Notes */}
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                    className="cursor-pointer"
                    onClick={() => setSelectedApp(2)}
                  >
                    <div className={`app-preview-card transition-all duration-300 ${selectedApp === 2 ? 'ring-2 ring-cyan-400 shadow-lg shadow-cyan-500/20' : 'opacity-70 hover:opacity-100'}`}>
                      <img src={APPS[2].logo} alt="Smart Notes AI Pro" className="w-14 h-14 rounded-2xl shadow-lg" />
                      <h3 className="text-white font-semibold mt-2 text-sm">{APPS[2].name}</h3>
                      <p className="text-slate-400 text-xs">{APPS[2].tagline}</p>
                    </div>
                  </motion.div>

                  {/* Deprem SOS */}
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
                    className="cursor-pointer"
                    onClick={() => setSelectedApp(1)}
                  >
                    <div className={`app-preview-card transition-all duration-300 ${selectedApp === 1 ? 'ring-2 ring-orange-400 shadow-lg shadow-orange-500/20' : 'opacity-70 hover:opacity-100'}`}>
                      <img src={APPS[1].logo} alt="Deprem S.O.S" className="w-14 h-14 rounded-2xl shadow-lg" />
                      <h3 className="text-white font-semibold mt-2 text-sm">{APPS[1].name}</h3>
                      <p className="text-slate-400 text-xs">{APPS[1].tagline}</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== SCREENSHOT CAROUSEL ====================
const AnimatedPhoneScreenshot = ({ src, alt, isActive }) => {
  const [stage, setStage] = useState('coding');
  const [codeText, setCodeText] = useState('');
  const fullCode = "import React from 'react';\nimport { View, Text } from 'react-native';\n\nexport default function App() {\n  return (\n    <View style={styles.container}>\n      <Text>Loading App...</Text>\n    </View>\n  );\n}";

  useEffect(() => {
    if (isActive) {
      setStage('coding');
      setCodeText('');
    } else {
      setStage('hidden');
    }
  }, [isActive, src]);

  useEffect(() => {
    if (stage === 'coding') {
      let i = 0;
      const interval = setInterval(() => {
        setCodeText(fullCode.substring(0, i));
        i += 2; // Write 2 characters at a time for even faster effect
        if (i > fullCode.length) {
          clearInterval(interval);
          setTimeout(() => setStage('showing-app'), 200);
        }
      }, 5);
      return () => clearInterval(interval);
    }
  }, [stage, fullCode]);

  return (
    <div className="relative w-full h-full bg-[#0d1117] rounded-[2rem] overflow-hidden">
      <AnimatePresence>
        {stage === 'coding' && (
          <motion.div
            key="coding"
            className="absolute inset-0 p-4 pt-12 bg-[#0d1117] font-mono text-[10px] sm:text-xs text-green-400 overflow-hidden text-left"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <pre className="whitespace-pre-wrap leading-relaxed">{codeText}<motion.span animate={{ opacity: [0, 1] }} transition={{ repeat: Infinity, duration: 0.8 }}>|</motion.span></pre>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {stage === 'showing-app' && (
          <motion.img
            key="app-img"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            src={src}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover rounded-[2rem]"
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ScreenshotCarousel = ({ screenshots, appName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  const scrollTo = (index) => {
    setCurrentIndex(index);
    if (scrollRef.current) {
      const child = scrollRef.current.children[index];
      if (child) {
        child.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };

  const prev = () => scrollTo(Math.max(0, currentIndex - 1));
  const next = () => scrollTo(Math.min(screenshots.length - 1, currentIndex + 1));

  return (
    <div className="screenshot-carousel">
      <div className="screenshot-carousel-track" ref={scrollRef}>
        {screenshots.map((src, i) => (
          <motion.div
            key={i}
            className={`screenshot-carousel-item ${i === currentIndex ? 'active' : ''}`}
            onClick={() => scrollTo(i)}
            whileHover={{ scale: 1.02 }}
          >
            <img src={src} alt={`${appName} ekran görüntüsü ${i + 1}`} className="w-full h-full object-cover" />
          </motion.div>
        ))}
      </div>
      {screenshots.length > 1 && (
        <>
          <button className="carousel-nav carousel-nav-left" onClick={prev} disabled={currentIndex === 0}>
            <ChevronLeft size={20} />
          </button>
          <button className="carousel-nav carousel-nav-right" onClick={next} disabled={currentIndex === screenshots.length - 1}>
            <ChevronRight size={20} />
          </button>
          <div className="carousel-dots">
            {screenshots.map((_, i) => (
              <button
                key={i}
                className={`carousel-dot ${i === currentIndex ? 'active' : ''}`}
                onClick={() => scrollTo(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ==================== APP CARD ====================
const AppCard = ({ app, index, featured }) => (
  <motion.div
    key={app.id}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.15 }}
    className={`app-card group ${featured ? 'app-card-featured' : ''}`}
    data-testid={`app-card-${app.id}`}
  >
    {/* Card Background */}
    <div className={`absolute inset-0 bg-gradient-to-br ${app.gradient} opacity-50 group-hover:opacity-70 transition-opacity`}></div>

    {/* Card Content */}
    <div className="relative z-10 p-8">
      <div className={`${featured ? 'lg:flex lg:gap-10' : ''}`}>
        <div className={`${featured ? 'lg:flex-1' : ''}`}>
          <div className="flex items-start gap-6 mb-6">
            <img
              src={app.logo}
              alt={app.name}
              className="w-24 h-24 rounded-3xl shadow-2xl group-hover:scale-105 transition-transform"
            />
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">{app.name}</h3>
              <p className="text-slate-300">{app.tagline}</p>
            </div>
          </div>

          <p className="text-slate-400 mb-6">{app.description}</p>

          {/* Features */}
          <div className="flex flex-wrap gap-2 mb-8">
            {app.features.map((feature, i) => (
              <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-sm text-slate-300">
                {feature}
              </span>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-4">
            {app.appStore && app.appStore !== "#" && (
              <a href={app.appStore} className="store-btn" target="_blank" rel="noopener noreferrer" data-testid={`${app.id}-appstore-btn`}>
                <Download size={18} />
                App Store
              </a>
            )}
            {app.playStore && app.playStore !== "#" && (
              <a href={app.playStore} className="store-btn" target="_blank" rel="noopener noreferrer" data-testid={`${app.id}-playstore-btn`}>
                <Download size={18} />
                Play Store
              </a>
            )}
            <Link to={app.promoUrl} className="promo-btn" data-testid={`${app.id}-promo-btn`}>
              <Play size={18} />
              Tanıtım Videosu
            </Link>
          </div>
        </div>

        {/* Screenshot Carousel for featured apps */}
        {featured && app.screenshots.length > 2 && (
          <div className="lg:flex-1 mt-8 lg:mt-0">
            <ScreenshotCarousel screenshots={app.screenshots} appName={app.name} />
          </div>
        )}
      </div>
    </div>

    {/* Screenshot Preview (non-featured only) */}
    {!featured && (
      <div className="absolute bottom-0 right-0 w-48 h-64 overflow-hidden opacity-20 group-hover:opacity-30 transition-opacity">
        <img
          src={app.screenshots[0]}
          alt=""
          className="w-full h-full object-cover object-top rounded-tl-3xl"
        />
      </div>
    )}
  </motion.div>
);

// ==================== APPS SECTION ====================
const AppsSection = () => {
  return (
    <section id="apps" className="py-24 relative z-10" data-testid="apps-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 font-medium mb-4 block">PORTFÖLYOMİZ</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Yayındaki Uygulamalarımız
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Kullanıcılarımızın hayatını kolaylaştıran, App Store ve Google Play'de yayında olan uygulamalarımız
          </p>
        </motion.div>

        {/* First two apps in grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {APPS.slice(0, 2).map((app, index) => (
            <AppCard key={app.id} app={app} index={index} featured={false} />
          ))}
        </div>

        {/* Third app featured full-width with carousel */}
        {APPS.length > 2 && (
          <AppCard app={APPS[2]} index={2} featured={true} />
        )}
      </div>
    </section>
  );
};

// ==================== PROJECTS SECTION ====================
const ProjectGallery = ({ project }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const trackRef = useRef(null);

  const scrollTo = (index) => {
    setCurrentIndex(index);
    if (trackRef.current && trackRef.current.children[index]) {
      trackRef.current.children[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  return (
    <>
      <div className="project-gallery">
        {/* Main Image */}
        <motion.div
          className="project-gallery-main cursor-pointer"
          onClick={() => setLightboxOpen(true)}
          whileHover={{ scale: 1.01 }}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={currentIndex}
              src={project.screenshots[currentIndex]}
              alt={`${project.name} - Ekran ${currentIndex + 1}`}
              className="w-full h-full object-contain rounded-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          </AnimatePresence>
          <div className="project-gallery-counter">{currentIndex + 1} / {project.screenshots.length}</div>
        </motion.div>

        {/* Thumbnails */}
        <div className="project-gallery-thumbs" ref={trackRef}>
          {project.screenshots.map((src, i) => (
            <button
              key={i}
              className={`project-thumb ${i === currentIndex ? 'active' : ''}`}
              onClick={() => scrollTo(i)}
            >
              <img src={src} alt="" />
            </button>
          ))}
        </div>

        {/* Navigation */}
        <button className="project-nav project-nav-left" onClick={() => scrollTo(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}>
          <ChevronLeft size={24} />
        </button>
        <button className="project-nav project-nav-right" onClick={() => scrollTo(Math.min(project.screenshots.length - 1, currentIndex + 1))} disabled={currentIndex === project.screenshots.length - 1}>
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            className="project-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxOpen(false)}
          >
            <button className="project-lightbox-close" onClick={() => setLightboxOpen(false)}><X size={28} /></button>
            <img src={project.screenshots[currentIndex]} alt="" className="project-lightbox-img" onClick={(e) => e.stopPropagation()} />
            <div className="project-lightbox-nav">
              <button onClick={(e) => { e.stopPropagation(); scrollTo(Math.max(0, currentIndex - 1)); }} disabled={currentIndex === 0}><ChevronLeft size={32} /></button>
              <span className="text-white text-lg">{currentIndex + 1} / {project.screenshots.length}</span>
              <button onClick={(e) => { e.stopPropagation(); scrollTo(Math.min(project.screenshots.length - 1, currentIndex + 1)); }} disabled={currentIndex === project.screenshots.length - 1}><ChevronRight size={32} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const ProjectsSection = () => {
  return (
    <section id="projects" className="py-24 relative z-10" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 font-medium mb-4 block">PROJELERİMİZ</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Web & Masaüstü Projeleri
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            İşletmelere özel geliştirdiğimiz profesyonel yazılım çözümleri
          </p>
        </motion.div>

        {PROJECTS.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 }}
            className="project-card"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-30`}></div>
            <div className="relative z-10">
              <div className="lg:flex lg:gap-10">
                {/* Project Info */}
                <div className="lg:w-2/5 p-8">
                  <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium mb-4">
                    {project.type}
                  </span>
                  <h3 className="text-3xl font-bold text-white mb-2">{project.name}</h3>
                  <p className="text-cyan-300 mb-4">{project.tagline}</p>
                  <p className="text-slate-400 mb-6 leading-relaxed">{project.description}</p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.features.map((feature, i) => (
                      <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-sm text-slate-300">
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, i) => (
                      <span key={i} className="px-3 py-1 bg-cyan-500/15 border border-cyan-500/30 rounded-lg text-sm text-cyan-300 font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Gallery */}
                <div className="lg:w-3/5 p-4">
                  <ProjectGallery project={project} />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// ==================== SERVICES SECTION ====================
const ServicesSection = () => {
  return (
    <section className="py-24 relative z-10" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 font-medium mb-4 block">HİZMETLERİMİZ</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Neler Yapıyoruz?
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="service-card"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-cyan-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-cyan-500/20 transition-colors">
                <service.icon className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
              <p className="text-slate-400">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== ABOUT SECTION ====================
const AboutSection = () => {
  return (
    <section id="about" className="py-24 relative z-10" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden glass-card neon-border flex items-center justify-center h-[400px] lg:h-[500px] group">
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 via-transparent to-cyan-400/10 group-hover:opacity-70 transition-opacity duration-500"></div>
              <img
                src="/mt_logo_1_binary.png"
                alt="MT Company Logo"
                className="relative z-10 w-48 h-48 md:w-64 md:h-64 rounded-3xl drop-shadow-[0_0_40px_rgba(34,211,238,0.3)] group-hover:scale-110 group-hover:drop-shadow-[0_0_60px_rgba(34,211,238,0.5)] transition-all duration-500"
              />
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-cyan-400 to-cyan-500 text-slate-900 p-6 rounded-2xl shadow-2xl">
              <div className="text-4xl font-bold">3+</div>
              <div className="text-sm opacity-80">Yıllık Deneyim</div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400 font-medium mb-4 block">HAKKIMIZDA</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Tutkulu Bir Ekip,<br />Kaliteli Çözümler
            </h2>
            <p className="text-lg text-slate-400 mb-6 leading-relaxed">
              MT Company olarak, mobil uygulama geliştirme alanında uzmanlaşmış bir ekibiz.
              Kullanıcı deneyimini ön planda tutarak, markaların dijital dünyada öne çıkmasını sağlıyoruz.
            </p>
            <p className="text-lg text-slate-400 mb-8 leading-relaxed">
              Her projede kaliteyi, yeniliği ve müşteri memnuniyetini öncelikli tutuyoruz.
              iOS ve Android platformları için native uygulamalar geliştiriyoruz.
            </p>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-slate-300">
                <Star className="text-cyan-400" size={20} />
                <span>Kaliteli Kod</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Users className="text-cyan-400" size={20} />
                <span>Müşteri Odaklı</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Rocket className="text-cyan-400" size={20} />
                <span>Hızlı Teslimat</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== CONTACT SECTION ====================
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    }, 1500);
  };

  return (
    <section id="contact" className="py-24 relative z-10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400 font-medium mb-4 block">İLETİŞİM</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Projenizi Konuşalım
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Bir fikriniz mi var? Birlikte harika bir uygulama oluşturalım.
              Bize ulaşın, size en kısa sürede dönüş yapalım.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <Mail className="text-cyan-400" size={24} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Email</div>
                  <div className="text-white">mt.company.app@gmail.com</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <Phone className="text-cyan-400" size={24} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Telefon</div>
                  <div className="text-white">+90 (xxx) xxx xx xx</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                  <MapPin className="text-cyan-400" size={24} />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Konum</div>
                  <div className="text-white">Türkiye</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="text-cyan-400" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Mesajınız Alındı!</h3>
                  <p className="text-slate-400">En kısa sürede size dönüş yapacağız.</p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">Adınız</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="form-input"
                      placeholder="Adınızı girin"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input"
                      placeholder="email@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">Mesajınız</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="form-input resize-none"
                      placeholder="Projeniz hakkında bilgi verin..."
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Gönderiliyor..." : "Mesaj Gönder"}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  return (
    <footer className="py-12 relative z-10 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/mt_logo_1_binary.png"
              alt="MT Company Logo"
              className="w-10 h-10 rounded-xl"
            />
            <span className="text-white font-semibold">MT Company</span>
          </div>

          <p className="text-slate-500 text-sm">
            © 2025 MT Company. Tüm hakları saklıdır.
          </p>

          <div className="flex items-center gap-4">
            <a href="#" className="text-slate-400 hover:text-white transition-colors">Twitter</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">LinkedIn</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">Instagram</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== SPLASH SCREEN ====================
const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('loading'); // loading, reveal

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => setPhase('reveal'), 400);
          setTimeout(() => onComplete(), 1200);
          return 100;
        }
        return prev + Math.random() * 3 + 1;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <motion.div
      className="splash-screen"
      animate={phase === 'reveal' ? { y: '-100%' } : {}}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
    >
      {/* Animated Background Grid */}
      <div className="splash-grid">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="splash-grid-line"
            style={{ left: `${(i + 1) * 5}%` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.03 }}
            transition={{ delay: i * 0.05 }}
          />
        ))}
      </div>

      {/* Floating Particles */}
      <div className="splash-particles">
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className="splash-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Center Content */}
      <div className="splash-content">
        {/* Phone Mockup */}
        <motion.div
          className="splash-phone"
          initial={{ opacity: 0, y: 60, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="splash-phone-notch"></div>
          <div className="splash-phone-screen">
            {/* Logo */}
            <motion.img
              src="/mt_logo_1_binary.png"
              alt="MT Company"
              className="splash-logo"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            />

            {/* Company Name */}
            <motion.h1
              className="splash-title"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              MT Company
            </motion.h1>

            {/* Tagline */}
            <motion.p
              className="splash-tagline"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              Uygulama Geliştiriyoruz...
            </motion.p>

            {/* Progress Bar */}
            <motion.div
              className="splash-progress-container"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: '80%' }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <motion.div
                className="splash-progress-bar"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </motion.div>

            {/* Percentage */}
            <motion.span
              className="splash-percent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.8 }}
            >
              {Math.min(Math.round(progress), 100)}%
            </motion.span>
          </div>
        </motion.div>

        {/* Code Lines Animation */}
        <div className="splash-code-lines">
          {['<App />', 'npm start', 'Building...', 'Deploying ✓'].map((text, i) => (
            <motion.div
              key={i}
              className="splash-code-line"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: progress > (i + 1) * 20 ? 0.4 : 0, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="splash-code-prefix">{'>'}</span> {text}
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ==================== MAIN PAGE ====================
const MainPage = () => {
  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <HeroSection />
      <AppsSection />
      <ProjectsSection />
      <ServicesSection />
      <AboutSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// ==================== PROMO PAGES (KEPT FROM BEFORE) ====================
// ... Previous promo video code stays the same ...

// App Screenshots and Logo URLs for Promo Videos
const EZAN_LOGO = "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/wtvdfl2g_iTunesArtwork%402x.png";
const EZAN_SCREENSHOTS = [
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/xaaj0axg_1.png", title: "Vakitler ve Daha Fazlası", description: "Namaz vakitlerini takip edin" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/scidc5c6_2.png", title: "Yakındaki Camilere Ulaşın", description: "Harita ile en yakın camileri bulun" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/199nl6o6_4.png", title: "Takvim ve Namaz Kılınışları", description: "İmsakiye ve namaz rehberi" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/696xp47k_5.png", title: "Sesli Dua ve Kıble", description: "Sesli dua dinleyin" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/gxvoro5y_8.png", title: "Zengin İçerik", description: "Kuran, hadisler ve daha fazlası" }
];
const EZAN_FEATURES = [
  { icon: "🕌", text: "Namaz Vakitleri" }, { icon: "🧭", text: "Kıble Pusulası" },
  { icon: "📍", text: "Cami Bulucu" }, { icon: "📖", text: "Kuran-ı Kerim" },
  { icon: "🤲", text: "Sesli Dualar" }, { icon: "📿", text: "Zikir Sayacı" },
  { icon: "📅", text: "İmsakiye" }, { icon: "✨", text: "Esmaül Hüsna" }
];

const DEPREM_LOGO = "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/mq8ee097_appstore.png";
const DEPREM_SCREENSHOTS = [
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/a2e9ndwx_1.png", title: "Acil Durumda Yanınızda", description: "Tek tuşla SOS gönder" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/u2gasuai_2.png", title: "Kullanışlı Özellikler", description: "Düdük, flaşör ve daha fazlası" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/3j16o5ed_3.png", title: "Yardım Butonları", description: "Enkaz altındayım, acil yardım" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/hkmk7hek_4.png", title: "Bluetooth Sinyal", description: "Yakındaki cihazlara sinyal" },
  { url: "https://customer-assets.emergentagent.com/job_brand-gallery-6/artifacts/py5gjm2l_5.png", title: "Deprem Bildirimleri", description: "Son depremleri takip edin" }
];
const DEPREM_FEATURES = [
  { icon: "🆘", text: "Acil SOS" }, { icon: "📍", text: "Konum Paylaş" },
  { icon: "📢", text: "Düdük" }, { icon: "🔦", text: "Flaşör" },
  { icon: "💡", text: "Ekran Işığı" }, { icon: "📡", text: "Bluetooth" },
  { icon: "🔔", text: "Deprem Alarmı" }, { icon: "📊", text: "Son Depremler" }
];

const EZAN_MUSIC = "/piano_meditation.mp3";
const DEPREM_MUSIC = "/piano_meditation.mp3";

const SMART_NOTES_LOGO = "/smart-notes-logo.png.png";
const SMART_NOTES_SCREENSHOTS = [
  { url: "/smart-notes-1.png.jpg", title: "Yatırım Portföyü", description: "Altın, döviz ve hisse yatırımlarınızı takip edin" },
  { url: "/smart-notes-2.png.jpg", title: "Gelir-Gider Takibi", description: "Haftalık ve aylık finansal özet" },
  { url: "/smart-notes-3.png.jpg", title: "Akıllı Ajanda", description: "Randevularınızı ve görevlerinizi yönetin" },
  { url: "/smart-notes-4.png.jpg", title: "AI Asistan", description: "Sesli ve yazılı komutlarla asistanınızı kullanın" }
];
const SMART_NOTES_FEATURES = [
  { icon: "📝", text: "Akıllı Notlar" }, { icon: "🤖", text: "AI Asistan" },
  { icon: "💰", text: "Finans Takibi" }, { icon: "📈", text: "Yatırım Portföyü" },
  { icon: "📅", text: "Ajanda" }, { icon: "🔔", text: "Bildirimler" },
  { icon: "🎤", text: "Sesli Komut" }, { icon: "📊", text: "Raporlar" }
];
const SMART_NOTES_MUSIC = "/piano_meditation.mp3";

// Promo Video Component
const PromoVideo = ({ appName, appTagline, logo, screenshots, features, music, theme }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showFeatures, setShowFeatures] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev >= screenshots.length - 1) { setIsPlaying(false); return prev; }
        return prev + 1;
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [isPlaying, screenshots.length]);

  useEffect(() => {
    if (isPlaying && showIntro) {
      const introTimer = setTimeout(() => { setShowIntro(false); setShowFeatures(true); }, 3000);
      return () => clearTimeout(introTimer);
    }
  }, [isPlaying, showIntro]);

  useEffect(() => {
    if (isPlaying && showFeatures) {
      const featuresTimer = setTimeout(() => { setShowFeatures(false); }, 4000);
      return () => clearTimeout(featuresTimer);
    }
  }, [isPlaying, showFeatures]);

  const startVideo = () => {
    setIsPlaying(true); setShowIntro(true); setCurrentSlide(0);
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(console.log); }
  };

  const restartVideo = () => {
    setCurrentSlide(0); setShowIntro(true); setShowFeatures(false); setIsPlaying(true);
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(console.log); }
  };

  const pauseVideo = () => { setIsPlaying(false); if (audioRef.current) audioRef.current.pause(); };
  const toggleMute = () => { setIsMuted(!isMuted); if (audioRef.current) audioRef.current.muted = !isMuted; };

  return (
    <div className={`promo-container ${theme}`} data-testid="promo-container">
      <audio ref={audioRef} src={music} loop muted={isMuted} />
      <div className={`promo-bg ${theme}`}><div className="stars"></div><div className="stars2"></div>
        {theme === 'ezan-theme' && <div className="crescent-moon"></div>}
        {theme === 'deprem-theme' && <div className="alert-pulse"></div>}
      </div>

      <div className="promo-wrapper">
        <header className={`promo-header ${isPlaying && showIntro ? 'header-intro' : ''}`}>
          <div className="promo-logo-container">
            <img src={logo} alt={appName} className={`promo-logo ${isPlaying && showIntro ? 'logo-animate' : ''}`} />
            <div className="promo-title-container">
              <h1 className={`promo-title ${isPlaying && showIntro ? 'title-animate' : ''}`}>{appName}</h1>
              <p className={`promo-tagline ${isPlaying && showIntro ? 'tagline-animate' : ''}`}>{appTagline}</p>
            </div>
          </div>
        </header>

        {isPlaying && showFeatures && (
          <div className="promo-features-overlay">
            <h2 className="promo-features-title">Tüm Özellikler Bir Arada</h2>
            <div className="promo-features-grid">
              {features.map((feature, index) => (
                <div key={index} className="promo-feature-item" style={{ animationDelay: `${index * 0.15}s` }}>
                  <span className="promo-feature-icon">{feature.icon}</span>
                  <span className="promo-feature-text">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isPlaying && !showIntro && !showFeatures && (
          <div className="promo-slideshow">
            {screenshots.map((screenshot, index) => (
              <div key={index} className={`promo-slide ${index === currentSlide ? 'active' : ''}`}>
                <div className="promo-slide-content">
                  <h2 className="promo-slide-title">{screenshot.title}</h2>
                  <p className="promo-slide-description">{screenshot.description}</p>
                  <img src={screenshot.url} alt={screenshot.title} className="promo-screenshot" />
                </div>
              </div>
            ))}
            <div className="promo-progress">
              {screenshots.map((_, index) => (
                <div key={index} className={`promo-dot ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`} onClick={() => setCurrentSlide(index)} />
              ))}
            </div>
          </div>
        )}

        {!isPlaying && currentSlide !== screenshots.length - 1 && (
          <div className="promo-start">
            <div className="promo-start-content">
              <img src={logo} alt={appName} className="promo-start-logo" />
              <h1 className="promo-start-title">{appName}</h1>
              <p className="promo-start-subtitle">Tanıtım Videosu</p>
              <button className="promo-play-btn" onClick={startVideo} data-testid="play-button">
                <Play size={24} /><span>Videoyu Başlat</span>
              </button>
            </div>
          </div>
        )}

        {isPlaying && (
          <div className="promo-controls">
            <button className="promo-control-btn" onClick={pauseVideo}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg></button>
            <button className="promo-control-btn" onClick={restartVideo}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg></button>
            <button className="promo-control-btn" onClick={toggleMute}>{isMuted ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" /></svg> : <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>}</button>
          </div>
        )}

        {!isPlaying && currentSlide === screenshots.length - 1 && (
          <div className="promo-end">
            <div className="promo-end-content">
              <img src={logo} alt={appName} className="promo-end-logo" />
              <h2 className="promo-end-title">{appName}</h2>
              <p className="promo-end-cta">Hemen İndirin!</p>
              <div className="promo-store-buttons">
                <button className="promo-store-btn app-store"><Download size={20} /><span>App Store</span></button>
                <button className="promo-store-btn play-store"><Download size={20} /><span>Play Store</span></button>
              </div>
              <button className="promo-replay-btn" onClick={restartVideo}><svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg><span>Tekrar İzle</span></button>
            </div>
          </div>
        )}
      </div>

      <footer className="promo-footer"><p>{appName} © 2025</p></footer>
    </div>
  );
};

const EzanVaktiPage = () => (
  <>
    <Link to="/" className="back-btn" data-testid="back-btn"><ArrowRight size={20} style={{ transform: 'rotate(180deg)' }} />Ana Sayfa</Link>
    <PromoVideo appName="Ezan Vakti Plus" appTagline="Manevi Hayatınızın Dijital Yol Arkadaşı" logo={EZAN_LOGO} screenshots={EZAN_SCREENSHOTS} features={EZAN_FEATURES} music={EZAN_MUSIC} theme="ezan-theme" />
  </>
);

const DepremSOSPage = () => (
  <>
    <Link to="/" className="back-btn" data-testid="back-btn"><ArrowRight size={20} style={{ transform: 'rotate(180deg)' }} />Ana Sayfa</Link>
    <PromoVideo appName="Deprem S.O.S" appTagline="Acil Durumda Yanınızda" logo={DEPREM_LOGO} screenshots={DEPREM_SCREENSHOTS} features={DEPREM_FEATURES} music={DEPREM_MUSIC} theme="deprem-theme" />
  </>
);

const SmartNotesPage = () => (
  <>
    <Link to="/" className="back-btn" data-testid="back-btn"><ArrowRight size={20} style={{ transform: 'rotate(180deg)' }} />Ana Sayfa</Link>
    <PromoVideo appName="Smart Notes AI Pro" appTagline="Akıllı Not, Finans, AI Asistan" logo={SMART_NOTES_LOGO} screenshots={SMART_NOTES_SCREENSHOTS} features={SMART_NOTES_FEATURES} music={SMART_NOTES_MUSIC} theme="smart-notes-theme" />
  </>
);

// ==================== MAIN APP ====================
function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <BrowserRouter>
      <AnimatePresence>
        {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      </AnimatePresence>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/ezan-vakti" element={<EzanVaktiPage />} />
        <Route path="/deprem-sos" element={<DepremSOSPage />} />
        <Route path="/smart-notes" element={<SmartNotesPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
